import React, { useState, useRef } from 'react';
import { BRA_SIZE_COLS, CLOTHES_SIZE_COLS, BRA_SIZE_COL_TERRITORIES, CLOTHES_SIZE_COL_TERRITORIES, SHEET_DEPT_META } from '../../config/constants';
import { F, C, btn, inp } from '../../config/styles';
import { useTenantConfig } from '../../hooks/useTenant';

// Fallback used when a tenant has no sizeCategories configured — the standard Bra / Clothes shape.
const FALLBACK_CATEGORIES = [
  { id: 'bra',     label: 'Bra Sizing',     subtitle: 'UK/US/CA/EU base size → territory equivalents', rowKey: 'UK Bra', cols: BRA_SIZE_COLS.map((label, i) => ({ label, territories: BRA_SIZE_COL_TERRITORIES[i] })) },
  { id: 'clothes', label: 'Clothes Sizing', subtitle: 'UK base size → territory equivalents',          rowKey: 'UK',     cols: CLOTHES_SIZE_COLS.map((label, i) => ({ label, territories: CLOTHES_SIZE_COL_TERRITORIES[i] })) },
];

function parseSizeCSV(text, cols) {
  const lines = text.trim().split(/\r?\n/).filter(Boolean);
  if (lines.length < 2) return null;
  const result = {};
  for (let i = 1; i < lines.length; i++) {
    const cells = lines[i].split(',').map(s => s.trim().replace(/^"|"$/g, ''));
    const key = cells[0];
    if (!key) continue;
    result[key] = cols.map((_, idx) => cells[idx + 1] ?? '');
  }
  return Object.keys(result).length ? result : null;
}

function SizeSection({ title, subtitle, rowKey, cols, table, onUpdate }) {
  const [editCell,     setEditCell]     = useState(null);
  const [editVal,      setEditVal]      = useState('');
  const [newUk,        setNewUk]        = useState('');
  const [importError,  setImportError]  = useState('');
  const fileRef = useRef();

  const rows = Object.keys(table);

  const startEdit = (uk, colIdx) => {
    setEditCell({ uk, colIdx });
    setEditVal(table[uk][colIdx] ?? '');
  };

  const commitEdit = () => {
    if (!editCell) return;
    const { uk, colIdx } = editCell;
    const newRow = [...table[uk]];
    newRow[colIdx] = editVal.trim();
    onUpdate({ ...table, [uk]: newRow });
    setEditCell(null);
  };

  const addRow = () => {
    const uk = newUk.trim();
    if (!uk || table[uk]) return;
    onUpdate({ ...table, [uk]: cols.map(() => '') });
    setNewUk('');
  };

  const removeRow = (uk) => {
    const next = { ...table };
    delete next[uk];
    onUpdate(next);
  };

  const handleImport = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setImportError('');
    const reader = new FileReader();
    reader.onload = (ev) => {
      const parsed = parseSizeCSV(ev.target.result, cols);
      if (!parsed) { setImportError('Could not parse CSV. Check the format and try again.'); return; }
      onUpdate({ ...table, ...parsed });
    };
    reader.readAsText(file);
  };

  const thS = {
    padding: '7px 14px', textAlign: 'center', fontWeight: 600, fontSize: 11,
    color: SHEET_DEPT_META.ops.fg, background: SHEET_DEPT_META.ops.hdr,
    borderBottom: `2px solid ${C.d0}`, whiteSpace: 'nowrap',
  };
  const tdS = (active) => ({
    padding: 0, borderBottom: `1px solid ${C.f4}`, textAlign: 'center',
    background: active ? '#fffdf0' : C.white,
    outline: active ? `2px solid #f59e0b` : 'none', outlineOffset: -2,
    cursor: 'text', minWidth: 80,
  });

  return (
    <div style={{ marginBottom: 40 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 8 }}>
        <div>
          <div style={{ fontFamily: F.display, fontSize: 20, color: C.text }}>{title}</div>
          {subtitle && <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>{subtitle}</div>}
        </div>
        <div>
          <input ref={fileRef} type="file" accept=".csv" style={{ display: 'none' }} onChange={handleImport} />
          <button style={{ ...btn('secondary'), fontSize: 12 }} onClick={() => { setImportError(''); fileRef.current?.click(); }}>
            Import CSV
          </button>
        </div>
      </div>

      <div style={{ overflowX: 'auto', borderRadius: 10, border: `1px solid ${C.e8}` }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, fontFamily: F.body }}>
          <thead>
            <tr>
              <th style={{ ...thS, textAlign: 'left', background: SHEET_DEPT_META.trading.hdr, color: SHEET_DEPT_META.trading.fg }}>
                {rowKey}
              </th>
              {cols.map((col, colIdx) => (
                <th key={colIdx} style={thS}>{col.label}</th>
              ))}
              <th style={{ ...thS, width: 32, background: C.fa }} />
            </tr>
          </thead>
          <tbody>
            {rows.map(uk => (
              <tr key={uk}>
                <td style={{ padding: '6px 14px', borderBottom: `1px solid ${C.f4}`, fontFamily: F.mono, fontSize: 12, fontWeight: 600, color: C.text, background: C.fa, minWidth: 100 }}>
                  {uk}
                </td>
                {cols.map((col, colIdx) => {
                  const isEdit = editCell?.uk === uk && editCell?.colIdx === colIdx;
                  const val = table[uk][colIdx] ?? '';
                  return (
                    <td key={colIdx} style={tdS(isEdit)} onClick={() => !isEdit && startEdit(uk, colIdx)}>
                      {isEdit ? (
                        <input
                          autoFocus
                          type="text"
                          value={editVal}
                          onChange={e => setEditVal(e.target.value)}
                          onBlur={commitEdit}
                          onKeyDown={e => {
                            if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); commitEdit(); }
                            if (e.key === 'Escape') setEditCell(null);
                          }}
                          style={{ width: '100%', height: 34, border: 'none', background: 'transparent', fontFamily: F.mono, fontSize: 12, color: C.text, padding: '0 10px', textAlign: 'center', outline: 'none' }}
                        />
                      ) : (
                        <div style={{ padding: '0 10px', height: 34, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: F.mono }}>
                          {val || <span style={{ color: C.ph }}>-</span>}
                        </div>
                      )}
                    </td>
                  );
                })}
                <td style={{ padding: '0 8px', borderBottom: `1px solid ${C.f4}`, textAlign: 'center', background: C.fa }}>
                  <button onClick={() => removeRow(uk)} title="Remove row" style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.muted, fontSize: 15, lineHeight: 1, padding: '0 2px' }}>×</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 12 }}>
        <input
          type="text"
          value={newUk}
          onChange={e => setNewUk(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && addRow()}
          placeholder={`New ${rowKey} size`}
          style={{ ...inp({ width: 260 }) }}
        />
        <button style={btn('secondary')} onClick={addRow}>+ Add size</button>
      </div>
      {importError && <div style={{ fontSize: 12, color: '#dc2626', marginTop: 8 }}>{importError}</div>}
    </div>
  );
}

export function SizeTableView({ sizeTable, onUpdate }) {
  const tenantConfig = useTenantConfig();
  const sizeCategories = tenantConfig?.sizeCategories?.length > 0
    ? tenantConfig.sizeCategories
    : FALLBACK_CATEGORIES;

  const clearAll = () => onUpdate(
    Object.fromEntries(sizeCategories.map(c => [c.id, {}]))
  );

  return (
    <div style={{ padding: '28px 32px', maxWidth: 860, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <div style={{ fontFamily: F.display, fontSize: 30, color: C.text }}>Size Table</div>
        <button style={btn('danger')} onClick={clearAll}>Clear all</button>
      </div>
      <div style={{ fontSize: 13, color: C.muted, marginBottom: 32 }}>
        Size equivalents per territory. Click any cell to edit. Changes apply immediately.
      </div>

      {sizeCategories.map(cat => (
        <SizeSection
          key={cat.id}
          title={cat.label}
          subtitle={cat.subtitle}
          rowKey={cat.rowKey}
          cols={cat.cols || []}
          table={sizeTable[cat.id] || {}}
          onUpdate={updated => onUpdate({ ...sizeTable, [cat.id]: updated })}
        />
      ))}

      {sizeCategories.length === 0 && (
        <div style={{ color: C.muted, fontSize: 14 }}>
          No size categories configured. Add categories in Workspace Settings.
        </div>
      )}
    </div>
  );
}
